import { FC } from 'react';

import DeleteOutline from '@mui/icons-material/DeleteOutline';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { GridColDef } from '@mui/x-data-grid-pro';

import DataGridTable from '@/shared/components/dataDisplay/DataGridTable/DataGridTable';
import useDataGridTable from '@/shared/components/dataDisplay/DataGridTable/hooks/useDataGridTable';
import TableInstance from '@/shared/constants/tableInstance';

import DescriptionCustomColumn from './components/DescriptionCustomColumn';
import PackageCuboidDimensionColumn from './components/PackageCuboidDimensionColumn';
import PackageWeightCustomColumn from './components/PackageWeightCustomColumn';
import usePackagingDetailsTableForm_v2 from './hooks/usePackagingDetailsTableForm_v2';
import Package from './types/package.type';

type PackagingDetailsTableForm_v2Props = {
  packagingDetailsTableForm_v2State: ReturnType<
    typeof usePackagingDetailsTableForm_v2
  >;
};

const PackagingDetailsTableForm_v2: FC<PackagingDetailsTableForm_v2Props> = ({
  packagingDetailsTableForm_v2State,
}) => {
  const { apiRef, model } = useDataGridTable();

  const {
    columns,
    packages,
    visibilityModel,
    setVisibilityModel,
    handleAddPackage,
    handleRemovePackage,
    handleUpdateWeight,
    handleUpdateCuboid,
    handleUpdateDescription,
  } = packagingDetailsTableForm_v2State;

  const customizedColumns: GridColDef[] = columns.map((column) => {
    const commonProps = {
      ...column,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      disableExport: true,
    };

    switch (column.field) {
      case 'package_number':
        return {
          ...commonProps,
          renderCell(params) {
            const row = params.row as Package;
            return (
              <Typography variant='body2'>
                Package {row.package_number}
              </Typography>
            );
          },
        };
      case 'package_assigned_items':
        return {
          ...commonProps,
          renderCell(params) {
            const row = params.row as Package;
            return (
              <Typography variant='body2'>
                {row.package_assigned_items} Items
              </Typography>
            );
          },
        };
      case 'package_weight':
        return {
          ...commonProps,
          renderCell(params) {
            const row = params.row as Package;
            return (
              <PackageWeightCustomColumn
                initialValue={row.package_weight}
                onChange={(value) =>
                  handleUpdateWeight({
                    package_id: row.package_id,
                    package_weight: value,
                  })
                }
              />
            );
          },
        };
      case 'package_dimensions':
        return {
          ...commonProps,
          width: 300,
          renderCell(params) {
            const row = params.row as Package;
            return (
              <PackageCuboidDimensionColumn
                initialValue={row.package_cuboid}
                onChange={(value) =>
                  handleUpdateCuboid({
                    package_id: row.package_id,
                    package_cuboid: value,
                  })
                }
              />
            );
          },
        };
      case 'package_description':
        return {
          ...commonProps,
          width: 300,
          renderCell(params) {
            const row = params.row as Package;
            return (
              <DescriptionCustomColumn
                initialValue={row.package_description}
                onChange={(value) =>
                  handleUpdateDescription({
                    package_id: row.package_id,
                    package_description: value,
                  })
                }
              />
            );
          },
        };
      case 'rowActions':
        return {
          ...commonProps,
          width: 50,
          renderHeader: () => null,
          renderCell: (column) => {
            const row = column.row as Package;
            return (
              <IconButton
                size='small'
                disabled={packages.length === 1}
                onClick={() => handleRemovePackage(row.package_id)}
              >
                <DeleteOutline sx={{ width: 18, height: 18 }} />
              </IconButton>
            );
          },
        };
      default:
        return column;
    }
  });

  return (
    <div style={{ height: '50vh', width: '100%' }}>
      <DataGridTable
        getRowId={(item) => item.package_id as string}
        paginationMode='client'
        filterMode='client'
        sortingMode='client'
        columns={customizedColumns}
        rows={packages}
        rowCount={packages.length}
        columnVisibilityModel={visibilityModel}
        onColumnVisibilityModelChange={setVisibilityModel}
        checkboxSelection={false}
        customProps={{
          tableInstance: TableInstance.Packages,
          apiRef,
          model,
          toolbar: {
            toolbarActions: (
              <Button variant='contained' onClick={handleAddPackage}>
                Add Package
              </Button>
            ),
          },
        }}
      />
    </div>
  );
};

export default PackagingDetailsTableForm_v2;
